import { useEffect, useRef, type ReactNode } from "react";
import type { PlannerProject, WardrobeModule } from "../types";
import { getShelvingGridLayout, getShelvingCellBoards, getShelvingCellLayouts } from "../domain/shelving";
import { usePlannerStore } from "../store/usePlannerStore";
import { normalizeLightTemperature, shelvingCellLighting } from "../domain/lighting";
import "./ShelvingLayoutEditor.css";

/**
 * Схема стеллажа: выбор ячейки для настройки её наполнения.
 *
 * Косые (диагональные) секции убраны по требованию заказчика — создать их
 * больше нельзя. Расчёт секций в домене сохранён, чтобы ранее сохранённые
 * проекты открывались и считались как раньше.
 */
export function ShelvingLayoutEditor({ module, project, onEditStructure, children }: {
  module: WardrobeModule; project: PlannerProject; onEditStructure: () => void;
  children?: ReactNode;
}) {
  const selectCell = usePlannerStore(state => state.selectShelvingCell);
  const gridRef = useRef<HTMLDivElement>(null);
  const selectionRef = useRef<HTMLDivElement>(null);
  const previousSelectionRef = useRef(project.selectedShelvingCellId);
  const grid = getShelvingGridLayout(module);
  const isUpper = Boolean(module.stackedOnId);
  // Объединённая ячейка — одна кнопка на всю её площадь.
  const layouts = getShelvingCellLayouts(module, grid);
  const cellIds = layouts.map(layout => layout.id);
  const selectedIndex = project.selectedShelvingCellId
    ? cellIds.indexOf(project.selectedShelvingCellId)
    : -1;

  useEffect(() => {
    if (cellIds.length > 0 && selectedIndex < 0) {
      selectCell(module.id, cellIds[0]!);
    }
  }, [cellIds.join("|"), module.id, selectCell, selectedIndex]);

  // Панель больше не прокручивается сама при выборе другой ячейки: схема и
  // настройки остаются там, где их видит человек (приёмка 04.10, sc-01/02).
  useEffect(() => {
    previousSelectionRef.current = project.selectedShelvingCellId;
  }, [project.selectedShelvingCellId]);

  const columnTemplate = grid.cellWidths.map(width => `${width}fr`).join(" ");
  // Ряды со своими ширинами (№ 19): ячейки рисуются по своим размерам, а не по общей сетке.
  const customRows = grid.rowCustomColumns.flatMap((custom, row) => custom ? [row + 1] : []);
  const free = customRows.length > 0;
  const innerLeft = grid.columnStarts[0] ?? -module.width / 2;
  const innerWidth = Math.max(1, module.width - 2 * (innerLeft + module.width / 2));
  const innerHeight = Math.max(1, grid.cellTop - grid.cellBottom);
  const freePlacement = (layout: { left: number; bottom: number; width: number; height: number }) => ({
    position: "absolute" as const,
    left: `calc(${((layout.left - innerLeft) / innerWidth) * 100}% + 2px)`,
    width: `calc(${(layout.width / innerWidth) * 100}% - 4px)`,
    bottom: `calc(${((layout.bottom - grid.cellBottom) / innerHeight) * 100}% + 2px)`,
    height: `calc(${(layout.height / innerHeight) * 100}% - 4px)`
  });
  const rowTemplate = [...grid.cellHeights].reverse().map(height => `${height}fr`).join(" ");
  const mapHeight = Math.max(grid.rows * 28, Math.min(330, 310 * module.height / module.width));

  const contentLabel = (column: number, row: number) => {
    const cell = module.shelvingCells?.find(item => item.column === column && item.row === row);
    return cell?.content === "door" ? "дверца" : cell?.content === "drawers" ? "ящики"
      : cell?.extraDivider ? "вертикальная перегородка" : cell?.extraShelf ? "дополнительная полка" : cell?.backPanel ? "задняя стенка" : "открытая";
  };

  return <div className="shelving-layout-editor">
    <div className="sl-heading"><strong>{isUpper ? "Схема антресоли" : "Схема стеллажа"}</strong>
      <span>Вид спереди · {module.width} × {module.height} × {module.depth} мм</span></div>
    <div className="sl-context-note">
      <div><strong>Здесь настраивается каждая ячейка</strong><span>{isUpper ? "Высота и сетка антресоли — на шаге «Размеры и сетка»." : "Общий размер, сетка и основание — на шаге «Размеры и сетка»."}</span></div>
      <button type="button" onClick={onEditStructure}>‹ Редактировать всё изделие</button>
    </div>
    <p className="sl-help" aria-live="polite">Выберите проём на крупной схеме или в 3D. Переключатели ниже меняют только выбранную ячейку.</p>
    {selectedIndex >= 0 && <div className="sl-cell-navigation" aria-label="Переход между ячейками">
      <button type="button" disabled={selectedIndex === 0}
        onClick={() => selectCell(module.id, cellIds[selectedIndex - 1]!)} aria-label="Предыдущая ячейка">←</button>
      <span><small>Выбрана</small><strong>Ячейка {selectedIndex + 1} из {cellIds.length}</strong></span>
      <button type="button" disabled={selectedIndex === cellIds.length - 1}
        onClick={() => selectCell(module.id, cellIds[selectedIndex + 1]!)} aria-label="Следующая ячейка">→</button>
    </div>}
    <div className="sl-map-wrap" style={{ ["--sl-map-height" as string]: `${mapHeight}px` }}>
      {/* Размеры колонок и рядов в свету — здесь, а не поверх 3D. */}
      {free
        ? <div className="sl-dims sl-dims--top sl-dims--note" aria-hidden="true">
            <span>{customRows.length === 1 ? `Ряд ${customRows[0]} — свои ширины` : `Ряды ${customRows.join(", ")} — свои ширины`}</span>
          </div>
        : <div className="sl-dims sl-dims--top" aria-hidden="true" style={{ gridTemplateColumns: columnTemplate }}>
        {grid.cellWidths.map((width, column) => <span key={column} className="sl-dim">
          <b>{column + 1}</b><i>{Math.round(width)}</i>
        </span>)}
      </div>}
      <div className="sl-dims sl-dims--side" aria-hidden="true" style={{ gridTemplateRows: rowTemplate }}>
        {[...grid.cellHeights].reverse().map((height, index) => <span key={index} className="sl-dim">
          <b>{grid.rows - index}</b><i>{Math.round(height)}</i>
        </span>)}
        {grid.baseHeight > 0 && <span className="sl-dim sl-dim--base">
          <i>{module.shelvingBase === "legs" ? "ножки" : "цоколь"} {Math.round(grid.baseHeight)}</i>
        </span>}
      </div>
      <div className={`sl-map${free ? " sl-map--free" : ""}`} ref={gridRef} role="group" aria-label={isUpper ? "Схема антресоли, ряды от нижней панели" : "Схема стеллажа, ряды от пола"}
      style={free ? { height: mapHeight } : { gridTemplateColumns: columnTemplate, gridTemplateRows: rowTemplate, height: mapHeight }}>
      <div className={free ? "sl-map__free" : "sl-map__grid"}>
      {layouts.map(layout => {
        {
          const { id, column, row } = layout;
          const label = contentLabel(column, row);
          const config = layout.config;
          const boards = config ? getShelvingCellBoards(config, layout.width, layout.height, module.depth) : [];
          const isSelected = project.selectedShelvingCellId === id;
          return <button type="button" key={id} style={free ? freePlacement(layout) : { gridColumn: `${column + 1} / span ${layout.columnSpan}`, gridRow: `${grid.rows - (row + layout.rowSpan - 1)} / span ${layout.rowSpan}` }}
            className={`sl-cell${isSelected ? " is-selected" : ""}`}
            data-content={label}
            data-light={shelvingCellLighting(config) !== "none" ? normalizeLightTemperature(config?.lightTemperature) : undefined}
            aria-label={`Колонка ${column + 1}, ряд ${row + 1}: ${label}${shelvingCellLighting(config) !== "none" ? ", с подсветкой" : ""}`}
            title={`Колонка ${column + 1}, ряд ${row + 1}: ${label}`}
            aria-pressed={isSelected}
            onClick={() => selectCell(module.id, id)}>
            {label === "дверца" || label === "ящики" ? <span aria-hidden="true">{label === "дверца" ? "▯" : "☰"}</span>
              : <svg className="sl-cell-boards" aria-hidden="true" viewBox={`0 0 ${layout.width} ${layout.height}`} preserveAspectRatio="none">
                {(config?.backPanel || module.shelvingBackPanel) && <rect width="100%" height="100%" fill="#d9c9b3" opacity="0.5" />}
                {boards.map((board, index) => {
                  const visibleWidth = board.projectedWidth ?? board.width;
                  return <rect key={index} x={board.x - visibleWidth / 2}
                    y={layout.height - board.y - board.height / 2}
                    width={visibleWidth} height={board.height} fill="#8d7962" />;
                })}
              </svg>}
          </button>;
        }
      })}
      </div>
    </div></div>
    <div className="sl-legend"><span>▯ Дверца</span><span>☰ Ящики</span><span>│ Перегородка</span><span>Размеры в свету, мм</span><span>{isUpper ? "Низ · над стеллажом" : "Низ · пол"}</span></div>

    <div ref={selectionRef}>{children}</div>
  </div>;
}
